import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { ACCOUNT_NUMBER_MAX_LENGTH, sanitizeBankNameInput } from '../../components/validations/validations';
import { validateAddBankForm } from '../../pages/contracts/contractDetailsHelpers';
import { createBankAccount } from '../../services/bank-account-services';
import { openToast } from '../../store/reducers/toastSlice';

interface UseAddBankAccountFormParams {
    /** Refresh the bank accounts list after a successful add. */
    getBankAccounts: () => Promise<void>;
}

// Initial/empty state for the add-bank-account form fields, shared between the initial
// useState value, the post-submit reset, and the drawer-close reset.
const getInitialFormData = () => ({
    accountType: '',
    nickName: '',
    bankName: '',
    routingNumber: '',
    reEnterRoutingNumber: '',
    accountNumber: '',
    reEnterAccountNumber: '',
    accountId: '',
});

/** Owns the "Add Bank Account" drawer form state and submit/validation handlers. */
export const useAddBankAccountForm = ({ getBankAccounts }: UseAddBankAccountFormParams) => {
    const dispatch = useDispatch();

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [formData, setFormData] = useState(getInitialFormData());
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [country, setCountry] = useState('');
    const [showAccountNumber, setShowAccountNumber] = useState(false);
    const [showReEnterAccountNumber, setShowReEnterAccountNumber] = useState(false);
    const [showRoutingNumber, setShowRoutingNumber] = useState(false);
    const [showReEnterRoutingNumber, setShowReEnterRoutingNumber] = useState(false);

    const accountTypeOptions = ['Checking', 'Savings'];

    // Shared toast dispatcher used by both the success and error/catch paths of the submit flow.
    const showToast = (message: string, severity: 'success' | 'error') => {
        dispatch(
            openToast({
                show: true,
                isAlertOpen: true,
                message,
                severity,
            }),
        );
    };

    // Shared reset used after a successful submit and when the drawer is closed.
    const resetFormState = () => {
        setFormData(getInitialFormData());
        setCountry('');
        setFormErrors({});
    };

    const handleInputChange = (field: string, value: string) => {
        // Account number fields: numeric only, max 17 digits
        // Bank name: allow only safe characters (no HTML, scripts, URLs)
        if (field === 'bankName' || field === 'nickName') {
            value = sanitizeBankNameInput(value);
        }
        // Account number fields: max 16 characters (matches backend CreateBankAccountDto)
        if (field === 'accountNumber' || field === 'reEnterAccountNumber') {
            value = value.replace(/[^0-9]/g, '').slice(0, ACCOUNT_NUMBER_MAX_LENGTH);
        }
        // Routing number fields: numeric only, max 9 digits
        if (field === 'routingNumber' || field === 'reEnterRoutingNumber') {
            value = value.replace(/\D/g, '').slice(0, 9);
        }
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
        // Clear error when user starts typing
        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: '',
            }));
        }
    };

    // Utility to map country name to code
    const getCountryCode = (country: string) => {
        const trimmed = country.trim();
        if (trimmed === 'United States') return 'USA';
        if (trimmed === 'Canada') return 'CAN';
        return trimmed;
    };

    const handleAddAccount = async () => {
        // Validate all required fields
        const errors = validateAddBankForm(formData, country);

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        try {
            // Call API to create bank account
            const payload = {
                bankName: formData.bankName.trim(),
                accountNumber: formData.accountNumber.trim(),
                country: getCountryCode(country),
                accountType: formData.accountType,
                routingNumber: formData.routingNumber.trim(),
                nickName: formData.nickName.trim() || null,
            };

            const response = await createBankAccount(payload, true);

            if (response.statusCode === 200 || response.statusCode === 201) {
                // Show success message
                showToast(response.message || 'Bank Account Added Successfully', 'success');

                // Reset form and close drawer
                resetFormState();
                setDrawerOpen(false);

                // Refresh bank accounts list
                await getBankAccounts();
            } else {
                // Show error message
                showToast(response.message || 'Failed to Add Bank Account', 'error');
            }
        } catch (error) {
            console.error('Error creating bank account:', error);
            showToast((error as Error)?.message || 'Failed to Add Bank Account. Please Try Again.', 'error');
        }
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        resetFormState();
        // Reset visibility states
        setShowAccountNumber(false);
        setShowReEnterAccountNumber(false);
        setShowRoutingNumber(false);
        setShowReEnterRoutingNumber(false);
    };

    return {
        drawerOpen,
        setDrawerOpen,
        formData,
        setFormData,
        formErrors,
        setFormErrors,
        country,
        setCountry,
        showAccountNumber,
        setShowAccountNumber,
        showReEnterAccountNumber,
        setShowReEnterAccountNumber,
        showRoutingNumber,
        setShowRoutingNumber,
        showReEnterRoutingNumber,
        setShowReEnterRoutingNumber,
        accountTypeOptions,
        handleInputChange,
        getCountryCode,
        handleAddAccount,
        handleCloseDrawer,
    };
};
